import api from './api';
import { ApiResponse } from '../types';
import {
  FeePaymentRow,
  FeePaymentDetail,
  FeePaymentListFilter,
  FeePaymentListResponse,
  RecordFeePaymentInput,
  RecordFeePaymentResult,
  CollectibleResponse,
  StudentsWithDuesFilter,
  StudentsWithDuesResponse,
  StudentFeeSummary,
  FeeActivityResponse,
} from '../types/fees';

const buildListParams = (filter: FeePaymentListFilter = {}) => {
  const sp = new URLSearchParams();
  if (filter.studentId)       sp.append('studentId', filter.studentId);
  if (filter.method)          sp.append('method', filter.method);
  if (filter.includeReversed) sp.append('includeReversed', 'true');
  if (filter.dateFrom)        sp.append('dateFrom', filter.dateFrom);
  if (filter.dateTo)          sp.append('dateTo', filter.dateTo);
  if (filter.search)          sp.append('search', filter.search);
  if (filter.page)            sp.append('page', String(filter.page));
  if (filter.limit)           sp.append('limit', String(filter.limit));
  return sp;
};

export const listFeePayments = async (filter: FeePaymentListFilter = {}): Promise<FeePaymentListResponse> => {
  const sp = buildListParams(filter);
  const response = await api.get<ApiResponse<FeePaymentListResponse>>(`/fee-payments?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getFeePayment = async (id: string): Promise<FeePaymentDetail> => {
  const response = await api.get<ApiResponse<FeePaymentDetail>>(`/fee-payments/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const recordFeePayment = async (input: RecordFeePaymentInput): Promise<RecordFeePaymentResult> => {
  const response = await api.post<ApiResponse<RecordFeePaymentResult>>('/fee-payments', input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const reverseFeePayment = async (id: string, reason: string): Promise<FeePaymentRow> => {
  const response = await api.post<ApiResponse<FeePaymentRow>>(`/fee-payments/${id}/reverse`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** Backward-compat: load collectible by studentId. Server resolves to Person. */
export const getCollectible = async (studentId: string): Promise<CollectibleResponse> => {
  const sp = new URLSearchParams({ studentId });
  const response = await api.get<ApiResponse<CollectibleResponse>>(`/fee-payments/collectible?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** Person-first collectible — the admission flow uses this directly. */
export const getCollectibleByPerson = async (personId: string): Promise<CollectibleResponse> => {
  const sp = new URLSearchParams({ personId });
  const response = await api.get<ApiResponse<CollectibleResponse>>(`/fee-payments/collectible?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getStudentsWithDues = async (filter: StudentsWithDuesFilter = {}): Promise<StudentsWithDuesResponse> => {
  const sp = new URLSearchParams();
  if (filter.sessionId)         sp.append('sessionId', filter.sessionId);
  if (filter.classId)           sp.append('classId', filter.classId);
  if (filter.sectionInstanceId) sp.append('sectionInstanceId', filter.sectionInstanceId);
  if (filter.search)            sp.append('search', filter.search);
  if (filter.overdueOnly)       sp.append('overdueOnly', 'true');
  if (filter.page)              sp.append('page', String(filter.page));
  if (filter.limit)             sp.append('limit', String(filter.limit));
  const response = await api.get<ApiResponse<StudentsWithDuesResponse>>(`/fee-payments/students-with-dues?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getStudentFeeSummary = async (studentId: string): Promise<StudentFeeSummary> => {
  const sp = new URLSearchParams({ studentId });
  const response = await api.get<ApiResponse<StudentFeeSummary>>(`/fee-payments/student-fee-summary?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getStudentFeeActivity = async (studentId: string, limit = 100): Promise<FeeActivityResponse> => {
  const sp = new URLSearchParams({ studentId, limit: String(limit) });
  const response = await api.get<ApiResponse<FeeActivityResponse>>(`/fee-payments/student-fee-activity?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

const feePaymentService = {
  listFeePayments,
  getFeePayment,
  recordFeePayment,
  reverseFeePayment,
  getCollectible,
  getCollectibleByPerson,
  getStudentsWithDues,
  getStudentFeeSummary,
  getStudentFeeActivity,
};

export default feePaymentService;
